/**
 * SB-8856 — locate + transform a subsession for the SubsessionModal.
 * Subsessions live nested under their parent in the payload, so the lookup
 * scans the sessions collection; widgets pass whatever they keep
 * (sessionsById values or a plain array).
 */

/**
 * @param {string} subsessionId
 * @param {Array|Object} sessions - array of sessions, or a sessionsById map
 * @returns {{subsession: Object, parent: Object} | null}
 */
export const findSubsession = (subsessionId, sessions) => {
  const list = Array.isArray(sessions) ? sessions : Object.values(sessions || {});
  for (const session of list) {
    const subsession = (session?.subsessions || []).find(candidate => candidate.id === subsessionId);
    if (subsession) return { subsession, parent: session };
  }
  return null;
};

/**
 * Shape a payload subsession for SubsessionModal — mirrors
 * getSessionDataForModal (date from range, ceuCredits, tagsList, speakers
 * hydrated from speakersById with SB-8853 role fields).
 */
export const getSubsessionDataForModal = (subsessionId, sessions, speakersById = {}) => {
  const found = findSubsession(subsessionId, sessions);
  if (!found) return null;
  const { subsession } = found;

  return {
    ...subsession,
    date: subsession.range,
    ceuCredits: subsession.ceu_credits,
    tags: subsession.tags?.map(tag => tag.name).join(', ') || '',
    tagsList: subsession.tags?.map(tag => tag.name) || [],
    speakers:
      subsession.speakers?.map(speaker => ({
        id: speaker.id,
        name: speaker.full_name,
        role: speaker.role,
        role_slug: speaker.role_slug || speaker.role || 'speaker',
        role_label: speaker.role_label || null,
        role_label_plural: speaker.role_label_plural || null,
        salutation: speakersById[speaker.id]?.salutation || speaker.salutation || '',
        honorific: speakersById[speaker.id]?.honorific || speaker.honorific || '',
        title: speakersById[speaker.id]?.title || '',
        company: speakersById[speaker.id]?.company_name || '',
        photo: speakersById[speaker.id]?.photo || '',
        // SB-9832: present only when the org shows current disclosures on embeds.
        disclosure: speakersById[speaker.id]?.disclosure || speaker.disclosure || null,
      })) || [],
  };
};
